// The redesign's page parts, so every screen is built from the same pieces: the page header
// (title, one-line purpose, actions), cards, stat cards, the one compact filter bar, status chips,
// segmented controls, link tabs, ids with a copy button, and the first-run empty state.
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { HelpButton, type HelpId } from "./help";
import { Icon } from "./icons";
import { Link } from "./link";

export function PageHeader({
  title,
  lede,
  actions,
  titleExtra,
  meta,
}: {
  title: ComponentChildren;
  /** What the screen is for, in one sentence. */
  lede?: ComponentChildren | undefined;
  actions?: ComponentChildren | undefined;
  /** Beside the title (a status chip). */
  titleExtra?: ComponentChildren | undefined;
  /** Under the lede (counts, the id with its copy button). */
  meta?: ComponentChildren | undefined;
}) {
  return (
    <header class="page-header">
      <div class="page-heading">
        <div class="page-title-row">
          <h1>{title}</h1>
          {titleExtra}
        </div>
        {lede && <p class="page-lede">{lede}</p>}
        {meta && <div class="page-meta">{meta}</div>}
      </div>
      {actions && <div class="page-actions">{actions}</div>}
    </header>
  );
}

export function Card({
  title,
  titleId,
  help,
  actions,
  children,
  class: className,
  label,
}: {
  title?: string | undefined;
  titleId?: string | undefined;
  /** A "?" beside the card title when the title names a concept. */
  help?: HelpId | undefined;
  actions?: ComponentChildren | undefined;
  children: ComponentChildren;
  class?: string | undefined;
  /** An accessible name for an untitled card region. */
  label?: string | undefined;
}) {
  const headingId = titleId ?? (title ? `card-${slug(title)}` : undefined);
  return (
    <section
      class={className ? `card ${className}` : "card"}
      aria-labelledby={title ? headingId : undefined}
      aria-label={title ? undefined : label}
    >
      {(title || actions) && (
        <div class="card-header">
          {title && (
            <div class="card-title-row">
              <h2 id={headingId}>{title}</h2>
              {help && <HelpButton topic={help} label={title} />}
            </div>
          )}
          {actions && <div class="row">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function StatGrid({ children, label }: { children: ComponentChildren; label?: string }) {
  return (
    <section class="stat-grid" aria-label={label}>
      {children}
    </section>
  );
}

export function StatCard({
  label,
  value,
  note,
  help,
  accent,
  secondary,
  notePositive,
}: {
  label: string;
  value: ComponentChildren;
  note?: ComponentChildren | undefined;
  help?: HelpId | undefined;
  /** Profit figures are blue. */
  accent?: boolean | undefined;
  /** A second figure beside the first (margin beside markup). */
  secondary?: ComponentChildren | undefined;
  notePositive?: boolean | undefined;
}) {
  return (
    <div class="stat-card">
      <span class="stat-label">
        {label}
        {help && <HelpButton topic={help} label={label} />}
      </span>
      {secondary === undefined ? (
        <span class={accent ? "stat-value stat-value-accent" : "stat-value"}>{value}</span>
      ) : (
        <span class="stat-pair">
          <span class={accent ? "stat-value stat-value-accent" : "stat-value"}>{value}</span>
          <span class="stat-secondary">{secondary}</span>
        </span>
      )}
      {note && <span class={notePositive ? "stat-note stat-note-positive" : "stat-note"}>{note}</span>}
    </div>
  );
}

/** The one compact filter bar at the top of a list or report. */
export function FilterBar({ children, end }: { children: ComponentChildren; end?: ComponentChildren }) {
  return (
    <div class="filter-bar" role="search" aria-label="Filters">
      {children}
      {end && <div class="filter-bar-end">{end}</div>}
    </div>
  );
}

export type ChipTone = "green" | "amber" | "grey" | "red" | "blue";

export function Chip({ tone = "grey", children, title }: { tone?: ChipTone | undefined; children: ComponentChildren; title?: string | undefined }) {
  return (
    <span class={`chip chip-${tone}`} title={title}>
      {children}
    </span>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: ComponentChildren;
}

/** A segmented choice for a small enum: aria-pressed buttons in a labelled group. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  labelledBy,
  label,
  small,
}: {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange(value: T): void;
  labelledBy?: string | undefined;
  label?: string | undefined;
  small?: boolean | undefined;
}) {
  return (
    <div class={small ? "segmented segmented-sm" : "segmented"} role="group" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label}>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

export interface TabLink {
  id: string;
  label: string;
  href: string;
  /** A count worth a glance beside the label (interventions, say); absent or 0 shows nothing. */
  badge?: number | undefined;
}

/**
 * Section tabs that are links (each tab has its own URL). `level="sub"` is the second row under a
 * section's tabs (`SubTabs`, REQ-237): the same links, styled smaller (`.tabs-sub`).
 */
export function LinkTabs({ tabs, current, label, level = "section" }: { tabs: readonly TabLink[]; current: string; label: string; level?: "section" | "sub" | undefined }) {
  return (
    <nav aria-label={label}>
      <ul class={level === "sub" ? "tabs tabs-sub" : "tabs"}>
        {tabs.map((tab) => (
          <li key={tab.id}>
            <Link href={tab.href} current={tab.id === current}>
              {tab.label}
              {tab.badge ? <Chip tone="amber">{tab.badge}</Chip> : null}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** In-page pill tabs (switching a table's breakdown without a URL change). */
export function PillTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  controls,
}: {
  tabs: readonly { value: T; label: string }[];
  value: T;
  onChange(value: T): void;
  label: string;
  /** The id of the panel the tabs switch. */
  controls?: string | undefined;
}) {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const index = tabs.findIndex((tab) => tab.value === value);
    const next = tabs[(index + (event.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length];
    if (!next) return;
    event.preventDefault();
    onChange(next.value);
    const list = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    requestAnimationFrame(() => list?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
  };
  return (
    <div class="pill-tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={tab.value === value}
          aria-controls={controls}
          tabIndex={tab.value === value ? 0 : -1}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/** An id shown small, with a button that copies it. */
export function CopyId({ id, label = "ID", what = "ID" }: { id: string; label?: string; what?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <span class="id-chip">
      {label && <span>{label}&nbsp;</span>}
      <span>{id}</span>
      <button
        type="button"
        class="copy-button"
        aria-label={copied ? `${what} copied` : `Copy ${what.toLowerCase() === "id" ? "ID" : what}`}
        title={copied ? "Copied" : `Copy ${id}`}
        onClick={() => {
          void navigator.clipboard?.writeText(id).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
      >
        <Icon name={copied ? "check" : "copy"} small />
      </button>
    </span>
  );
}

/** A name, linked when there is somewhere to go; the id only on hover, never as text. */
export function NameCell({ name, id, href }: { name: string; id: string; href?: string }) {
  return <span title={id}>{href ? <Link href={href}>{name}</Link> : name}</span>;
}

export interface EmptyStep {
  title: string;
  text: ComponentChildren;
}

/** A screen's first-run state: what it is for, the steps, and the first action. */
export function EmptyState({
  title,
  lede,
  steps,
  actions,
  headingLevel = 2,
}: {
  title: string;
  lede: ComponentChildren;
  steps?: readonly EmptyStep[] | undefined;
  actions?: ComponentChildren | undefined;
  headingLevel?: 2 | 3 | undefined;
}) {
  return (
    <section class="empty-state">
      <div class="stack-sm">
        {headingLevel === 2 ? <h2>{title}</h2> : <h3>{title}</h3>}
        <p class="empty-state-lede">{lede}</p>
      </div>
      {steps && steps.length > 0 && (
        <ol class="empty-steps">
          {steps.map((step, i) => (
            <li key={step.title}>
              <span class="empty-step-number" aria-hidden="true">
                {i + 1}
              </span>
              <span class="empty-step-title">{step.title}</span>
              <span class="empty-step-text">{step.text}</span>
            </li>
          ))}
        </ol>
      )}
      {actions && <div class="empty-state-actions">{actions}</div>}
    </section>
  );
}

/** A small share-of-total bar for a table cell (SVG: the CSP forbids style attributes). */
export function ShareBar({ fraction, label }: { fraction: number; label: string }) {
  const width = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0)) * 90;
  return (
    <svg class="share-bar" viewBox="0 0 90 6" role="img" aria-label={label}>
      <rect class="share-bar-track" x="0" y="0" width="90" height="6" rx="3" />
      {width > 0 && <rect class="share-bar-fill" x="0" y="0" width={Math.max(3, width)} height="6" rx="3" />}
    </svg>
  );
}
